import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { trackEvent, EVENT_TYPE_CTA, EVENT_TYPE_PAGE } from "../utils/trackEvent.jsx";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { MapPin, Phone, Star } from "lucide-react";
import { SERVICE_TYPES } from '../ServiceProvidersData.jsx';

const ServiceList = ({ serviceType, providers }) => {
  const navigate = useNavigate();
  const [selectedFilters, setSelectedFilters] = useState([]);

  const allTypes = useMemo(() => {
    const types = new Set();
    providers.forEach(p => p.types.forEach(t => types.add(t)));
    return Array.from(types);
  }, [providers]);

  const handleFilterChange = (type) => {
    setSelectedFilters(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  const filteredProviders = useMemo(() => {
    if (selectedFilters.length === 0) {
      return providers;
    }
    return providers.filter(p =>
      selectedFilters.every(filter => p.types.includes(filter))
    );
  }, [providers, selectedFilters]);

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Banner Section */}
      <div className="relative h-64 md:h-80 lg:h-96 overflow-hidden">
        <img
          src="/truD.avif"
          alt="Services banner"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-white text-3xl md:text-5xl font-bold mb-4">
              Our Service Providers
            </h1>
            <p className="text-white text-lg md:text-xl opacity-90 max-w-2xl mx-auto">
              Connect with trusted halal service providers for your business needs
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row justify-between md:items-center mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-[#1a4a1a] mb-4 md:mb-0">
            Available Services
          </h2>
          {/*<div className="flex items-center gap-4">
            <Button variant="outline">
              <MapPin className="mr-2 h-4 w-4" /> Near Me
            </Button>
          </div>*/}
        </div>

        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {allTypes.map(type => (
                <div key={type} className="flex items-center space-x-2">
                  <Checkbox
                    id={type}
                    checked={selectedFilters.includes(type)}
                    onCheckedChange={() => handleFilterChange(type)}
                  />
                  <label
                    htmlFor={type}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {type}
                  </label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          {filteredProviders.map(provider => (
            <Card
              key={provider.id}
              className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(`/service/${provider.id}`)}
            >
              <CardContent className="p-4 flex flex-col sm:flex-row gap-6">
                <img
                  src={provider.logo || "https://via.placeholder.com/150"}
                  alt={`${provider.name} logo`}
                  className="w-full sm:w-48 h-48 sm:h-auto object-cover rounded-lg"
                />
                <div className="flex-grow">
                  <Badge variant="secondary" className="mb-2 bg-green-100 text-green-800">SERVICE PROVIDERS</Badge>
                  <h2 className="text-2xl font-bold text-gray-900">{provider.name}</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {provider.types.join(', ')}
                  </p>
                  {provider.rating && (
                     <div className="flex items-center gap-1 mt-2">
                       <Star className="text-yellow-400 fill-yellow-400 h-5 w-5" />
                       <span className="font-semibold">{provider.rating}</span>
                     </div>
                  )}
                   <p className="text-sm text-gray-500 mt-2">
                    <MapPin className="inline-block mr-1 h-4 w-4" />
                    {provider.location || 'Location not available'}
                  </p>
                  <div className="mt-4 border-t pt-4">
                     <Button variant="outline" size="sm" onClick={(e) => {
                         e.stopPropagation();
                         window.location.href = `tel:${provider.contactInfo.phone}`;
                     }}>
                         <Phone className="mr-2 h-4 w-4" /> Call
                     </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ServiceList; 